/**
 * Environment Configuration
 * Centralizes environment variables and configuration
 */

import { BRAND } from './brand';

// API Configuration
export const API_CONFIG = {
  DJANGO_URL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
  API_TIMEOUT: import.meta.env.VITE_API_TIMEOUT || 30000,
  WS_URL: import.meta.env.VITE_WS_URL || 'ws://localhost:8000',
};

// App Configuration
export const APP_CONFIG = {
  APP_NAME: BRAND.productName,
  APP_VERSION: import.meta.env.VITE_APP_VERSION || '1.0.0',
  ENVIRONMENT: import.meta.env.MODE || 'development',
  IS_PRODUCTION: import.meta.env.MODE === 'production',
  IS_DEVELOPMENT: import.meta.env.MODE === 'development',
  IS_TEST: import.meta.env.MODE === 'test',
};

// Feature Flags
export const FEATURE_FLAGS = {
  ENABLE_MOCKS: import.meta.env.VITE_ENABLE_MOCKS === 'true',
  ENABLE_AI_FEATURES: import.meta.env.VITE_ENABLE_AI === 'true',
  ENABLE_BLOCKCHAIN: import.meta.env.VITE_ENABLE_BLOCKCHAIN === 'true',
  ENABLE_REAL_TIME: import.meta.env.VITE_ENABLE_REAL_TIME !== 'false', // Default true
  ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  ENABLE_USERBACK: import.meta.env.VITE_ENABLE_USERBACK === 'true',
  ENABLE_DEBUG_TOOLS: import.meta.env.MODE === 'development',
};

// Third-party integrations
export const THIRD_PARTY_CONFIG = {
  USERBACK_TOKEN: import.meta.env.VITE_USERBACK_TOKEN || '',
};

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
  MAX_PAGE_SIZE: 100,
};

// Real-time Updates
export const REALTIME_CONFIG = {
  POLLING_INTERVAL: 30000, // 30 seconds
  RECONNECT_INTERVAL: 5000, // 5 seconds
  MAX_RECONNECT_ATTEMPTS: 5,
};

// Cache TTL (Time To Live)
export const CACHE_TTL = {
  SHORT: 60, // 1 minute
  MEDIUM: 300, // 5 minutes
  LONG: 3600, // 1 hour
  VERY_LONG: 86400, // 24 hours
};

// File Upload
export const UPLOAD_CONFIG = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  MAX_FILES: 10,
  ALLOWED_TYPES: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
    'image/gif',
  ],
};

// Toast Notifications
export const TOAST_CONFIG = {
  DURATION: 5000, // 5 seconds
  MAX_TOASTS: 3,
  POSITION: 'top-right',
};

// Local Storage Keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  TENANT_ID: 'tenant_id',
  USER_DATA: 'user_data',
  THEME: 'theme',
  SIDEBAR_COLLAPSED: 'sidebar_collapsed',
  LAST_ROUTE: 'last_route',
};

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM DD, YYYY',
  DISPLAY_WITH_TIME: 'MMM DD, YYYY HH:mm',
  ISO: 'YYYY-MM-DD',
  ISO_WITH_TIME: 'YYYY-MM-DD HH:mm:ss',
  RELATIVE: 'relative', // Uses moment.fromNow() or similar
};

// Risk Levels
export const RISK_LEVELS = {
  CRITICAL: { label: 'Critical', color: '#EF4444', range: [20, 25] },
  HIGH: { label: 'High', color: '#F59E0B', range: [15, 19] },
  MEDIUM: { label: 'Medium', color: '#2E5E99', range: [9, 14] }, // Mapped to Secondary/Info
  LOW: { label: 'Low', color: '#22C55E', range: [5, 8] },
  VERY_LOW: { label: 'Very Low', color: '#7BA4D0', range: [1, 4] }, // Mapped to Primary/Ocean Blue
};

// Subscription Tiers
export const SUBSCRIPTION_TIERS = {
  BASIC: 'basic',
  PROFESSIONAL: 'professional',
  ENTERPRISE: 'enterprise',
};

// Permission Prefixes
export const PERMISSION_PREFIXES = {
  RISK: 'risk',
  COMPLIANCE: 'compliance',
  AUDIT: 'audit',
  GOVERNANCE: 'governance',
  ETHICS: 'ethics',
  IT_GOV: 'it_governance',
  AI_GOV: 'ai_governance',
  BLOCKCHAIN: 'blockchain',
  ADMIN: 'admin',
};

// Permission Actions
export const PERMISSION_ACTIONS = {
  READ: 'read',
  WRITE: 'write',
  DELETE: 'delete',
  APPROVE: 'approve',
  EXPORT: 'export',
};

// Build permission string helper
export const buildPermission = (prefix, action) => `${prefix}:${action}`;

// Country-Currency Mappings (African focus)
export const COUNTRY_CURRENCY_MAP = {
  Kenya: { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', locale: 'en-KE' },
  Nigeria: { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', locale: 'en-NG' },
  Ghana: { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi', locale: 'en-GH' },
  'South Africa': { code: 'ZAR', symbol: 'R', name: 'South African Rand', locale: 'en-ZA' },
  Tanzania: { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling', locale: 'en-TZ' },
  Uganda: { code: 'UGX', symbol: 'USh', name: 'Ugandan Shilling', locale: 'en-UG' },
  Rwanda: { code: 'RWF', symbol: 'RF', name: 'Rwandan Franc', locale: 'en-RW' },
  Ethiopia: { code: 'ETB', symbol: 'Br', name: 'Ethiopian Birr', locale: 'en-ET' },
  Zambia: { code: 'ZMW', symbol: 'ZK', name: 'Zambian Kwacha', locale: 'en-ZM' },
  Botswana: { code: 'BWP', symbol: 'P', name: 'Botswana Pula', locale: 'en-BW' },
  Mozambique: { code: 'MZN', symbol: 'MT', name: 'Mozambican Metical', locale: 'pt-MZ' },
  Zimbabwe: { code: 'ZWL', symbol: 'Z$', name: 'Zimbabwean Dollar', locale: 'en-ZW' },
  Mauritius: { code: 'MUR', symbol: '₨', name: 'Mauritian Rupee', locale: 'en-MU' },
  "Côte d'Ivoire": { code: 'XOF', symbol: 'CFA', name: 'West African CFA Franc', locale: 'fr-CI' },
  Senegal: { code: 'XOF', symbol: 'CFA', name: 'West African CFA Franc', locale: 'fr-SN' },
  Cameroon: { code: 'XAF', symbol: 'FCFA', name: 'Central African CFA Franc', locale: 'fr-CM' },
  Egypt: { code: 'EGP', symbol: 'E£', name: 'Egyptian Pound', locale: 'ar-EG' },
  Morocco: { code: 'MAD', symbol: 'DH', name: 'Moroccan Dirham', locale: 'ar-MA' },
  Algeria: { code: 'DZD', symbol: 'DA', name: 'Algerian Dinar', locale: 'ar-DZ' },
  Tunisia: { code: 'TND', symbol: 'DT', name: 'Tunisian Dinar', locale: 'ar-TN' },
  'United States': { code: 'USD', symbol: '$', name: 'US Dollar', locale: 'en-US' },
  'United Kingdom': { code: 'GBP', symbol: '£', name: 'British Pound', locale: 'en-GB' },
  'European Union': { code: 'EUR', symbol: '€', name: 'Euro', locale: 'en-EU' },
};

// Default Locale Configuration
export const LOCALE_CONFIG = {
  // Owner rule (2026-10-13): Nigeria and the Naira unless the company chooses otherwise.
  DEFAULT_COUNTRY: 'Nigeria',
  DEFAULT_CURRENCY: 'NGN',
  DEFAULT_LOCALE: 'en-NG',
  DEFAULT_TIMEZONE: 'Africa/Lagos',
};

// Export all as default
export default {
  API_CONFIG,
  APP_CONFIG,
  FEATURE_FLAGS,
  THIRD_PARTY_CONFIG,
  PAGINATION,
  REALTIME_CONFIG,
  CACHE_TTL,
  UPLOAD_CONFIG,
  TOAST_CONFIG,
  STORAGE_KEYS,
  DATE_FORMATS,
  RISK_LEVELS,
  SUBSCRIPTION_TIERS,
  PERMISSION_PREFIXES,
  PERMISSION_ACTIONS,
  buildPermission,
  COUNTRY_CURRENCY_MAP,
  LOCALE_CONFIG,
};
